import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Select, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';

const { Title } = Typography;
const { Option } = Select;

const Categories = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [typeFilter, setTypeFilter] = useState('');

  const typeMap = {
    1: '物料分类',
    2: '产品分类',
  };

  const fetchCategories = useCallback(async (page = 1, pageSize = 10, type = '') => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (type) params.type = type;
      
      const response = await api.get('/v1/categories', { params });
      setData(response.data.data.map(item => ({ ...item, key: item.id })));
      setPagination({ current: page, pageSize, total: response.data.total });
    } catch (error) {
      message.error('获取分类列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories(pagination.current, pagination.pageSize, typeFilter);
  }, []);

  const handleTableChange = (newPagination) => {
    fetchCategories(newPagination.current, newPagination.pageSize, typeFilter);
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
    fetchCategories(1, pagination.pageSize, value);
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    form.setFieldsValue(record || { name: '', type: 1 });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await api.put(`/v1/categories/${editingRecord.id}`, values);
        message.success('分类更新成功');
      } else {
        await api.post('/v1/categories', values);
        message.success('分类创建成功');
      }
      handleCancel();
      fetchCategories(pagination.current, pagination.pageSize, typeFilter);
    } catch (errorInfo) {
      const errorMsg = errorInfo.response?.data?.error || '操作失败';
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/categories/${id}`);
      message.success('分类删除成功');
      fetchCategories(pagination.current, pagination.pageSize, typeFilter);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '分类名称', dataIndex: 'name', key: 'name' },
    { title: '分类类型', dataIndex: 'type', key: 'type', render: (type) => typeMap[type] || type },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small" style={{ marginLeft: -8 }}>
          <Button type="link" size="small" onClick={() => showModal(record)}>编辑</Button>
          <Popconfirm title="确定删除此分类吗？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>分类管理</Title>
        <Space>
          <Select
            placeholder="分类类型"
            value={typeFilter || undefined}
            onChange={handleTypeFilterChange}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="1">物料分类</Option>
            <Option value="2">产品分类</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增分类
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        bordered
        size="small"
      />

      <Modal
        title={editingRecord ? '编辑分类' : '新增分类'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="category_form">
          <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="分类类型" rules={[{ required: true, message: '请选择分类类型!' }]}>
            <Select>
              <Option value={1}>物料分类</Option>
              <Option value={2}>产品分类</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
